import ExcelJS from 'exceljs';

export const RECORD_EXCEL_DATA_SHEET = 'Данные';
export const RECORD_EXCEL_HEADER_ROW = 6;
export const RECORD_EXCEL_DATA_START_ROW = 7;

export type RecordExcelDbTable =
  | 'companies'
  | 'sectors'
  | 'compensation_records'
  | 'compensation_record_base'
  | 'compensation_record_sti'
  | 'compensation_record_ltip'
  | 'person_assessments'
  | 'roles';

export type RecordExcelColumnDef = {
  col: number;
  letter: string;
  excelHeader: string;
  dbTable: RecordExcelDbTable;
  dbField: string;
  label: string;
  valueKind?: 'direct' | 'lookup' | 'derived';
};

/** Только согласованные колонки Excel → БД. */
export const RECORD_EXCEL_MAPPED_COLUMNS: RecordExcelColumnDef[] = [
  { col: 1, letter: 'A', excelHeader: 'Название компании', dbTable: 'companies', dbField: 'name', label: 'Название компании', valueKind: 'lookup' },
  { col: 7, letter: 'G', excelHeader: 'Сектор', dbTable: 'sectors', dbField: 'name', label: 'Сектор', valueKind: 'lookup' },
  { col: 8, letter: 'H', excelHeader: 'Название должности', dbTable: 'compensation_records', dbField: 'company_role_name', label: 'Название должности (в компании)' },
  { col: 22, letter: 'V', excelHeader: 'datacircle грейд', dbTable: 'roles', dbField: 'grade_level', label: 'Грейд datacircle', valueKind: 'lookup' },
  { col: 23, letter: 'W', excelHeader: 'Название функции / Семья', dbTable: 'person_assessments', dbField: 'family_id', label: 'Семья (family)', valueKind: 'lookup' },
  { col: 26, letter: 'Z', excelHeader: 'Референтная должность datacircle', dbTable: 'compensation_records', dbField: 'role_id', label: 'Роль по грейду datacircle', valueKind: 'lookup' },
  { col: 31, letter: 'AE', excelHeader: 'Ежемесячный оклад', dbTable: 'compensation_record_base', dbField: 'base_gross_salary', label: 'Базовый оклад', valueKind: 'derived' },
  { col: 41, letter: 'AO', excelHeader: 'Целевой размер премии в % от годового базового оклада', dbTable: 'compensation_record_sti', dbField: 'sti_target_pct', label: 'Целевой STI, %' },
  { col: 50, letter: 'AX', excelHeader: 'Форма LTIP', dbTable: 'compensation_record_ltip', dbField: 'ltip_form', label: 'Форма LTIP' },
];

export type RecordExcelParsedValues = {
  companyName: string | null;
  sectorName: string | null;
  companyRoleName: string | null;
  datacircleGrade: string | null;
  familyName: string | null;
  roleName: string | null;
  /** Значение из AE (в файле — месячный оклад) */
  baseSalaryMonthly: number | null;
  /** Годовой gross для compensation_record_base.base_gross_salary */
  baseGrossSalary: number | null;
  stiTargetPct: number | null;
  ltipForm: string | null;
};

export type RecordExcelParsedRow = {
  rowNumber: number;
  values: RecordExcelParsedValues;
  warnings: string[];
};

export type RecordExcelParseResult = {
  fileName: string;
  sheetName: string;
  headerRow: number;
  dataStartRow: number;
  rows: RecordExcelParsedRow[];
  headerWarnings: string[];
};

const EMPTY_TOKENS = new Set(['', '-', '—', 'n/a', 'na', 'нет данных']);

function normalizeHeader(value: string): string {
  return value.replace(/\s+/g, ' ').trim().toLowerCase();
}

function cellToRawString(value: ExcelJS.CellValue): string {
  if (value == null) return '';
  if (value instanceof Date) {
    return value.toLocaleDateString('ru-RU');
  }
  if (typeof value === 'object') {
    if ('richText' in value && Array.isArray(value.richText)) {
      return value.richText.map((part) => part.text).join('');
    }
    if ('text' in value && typeof value.text === 'string') return value.text;
    if ('result' in value && value.result != null) return String(value.result);
    if ('formula' in value && value.result != null) return String(value.result);
  }
  return String(value);
}

function parseOptionalText(raw: string): string | null {
  const trimmed = raw.replace(/\s+/g, ' ').trim();
  if (EMPTY_TOKENS.has(trimmed.toLowerCase())) return null;
  return trimmed || null;
}

function parseOptionalNumber(raw: string): number | null {
  const text = parseOptionalText(raw);
  if (text == null) return null;
  const normalized = text.replace(/\s/g, '').replace(',', '.');
  const n = Number(normalized);
  return Number.isFinite(n) ? n : null;
}

function getCellString(row: ExcelJS.Row, col: number): string {
  return cellToRawString(row.getCell(col).value);
}

function buildValues(row: ExcelJS.Row): RecordExcelParsedValues {
  const baseSalaryMonthly = parseOptionalNumber(getCellString(row, 31));

  return {
    companyName: parseOptionalText(getCellString(row, 1)),
    sectorName: parseOptionalText(getCellString(row, 7)),
    companyRoleName: parseOptionalText(getCellString(row, 8)),
    datacircleGrade: parseOptionalText(getCellString(row, 22)),
    familyName: parseOptionalText(getCellString(row, 23)),
    roleName: parseOptionalText(getCellString(row, 26)),
    baseSalaryMonthly,
    baseGrossSalary: baseSalaryMonthly != null ? Math.round(baseSalaryMonthly * 12) : null,
    stiTargetPct: parseOptionalNumber(getCellString(row, 41)),
    ltipForm: parseOptionalText(getCellString(row, 50)),
  };
}

function rowWarnings(values: RecordExcelParsedValues): string[] {
  const warnings: string[] = [];
  if (!values.companyName) warnings.push('Не указано название компании (A)');
  if (!values.sectorName) warnings.push('Не указан сектор (G)');
  if (!values.companyRoleName) warnings.push('Не указано название должности (H)');
  if (!values.roleName && !values.datacircleGrade) {
    warnings.push('Не указаны грейд (V) или роль datacircle (Z)');
  }
  if (!values.roleName && values.datacircleGrade && !values.familyName) {
    warnings.push('Указан грейд (V), но не указана семья (W) — роль не будет подобрана автоматически');
  }
  return warnings;
}

function isRowEmpty(values: RecordExcelParsedValues): boolean {
  return !values.companyName
    && !values.companyRoleName
    && values.baseSalaryMonthly == null
    && !values.roleName
    && !values.datacircleGrade;
}

function validateHeaders(sheet: ExcelJS.Worksheet): string[] {
  const headerRow = sheet.getRow(RECORD_EXCEL_HEADER_ROW);
  const warnings: string[] = [];

  for (const def of RECORD_EXCEL_MAPPED_COLUMNS) {
    const actual = normalizeHeader(getCellString(headerRow, def.col));
    const expected = normalizeHeader(def.excelHeader);
    if (!actual) {
      warnings.push(`Колонка ${def.letter}: заголовок пуст — ожидалось «${def.excelHeader}»`);
      continue;
    }
    if (!actual.includes(expected.slice(0, Math.min(16, expected.length)))) {
      warnings.push(`Колонка ${def.letter}: заголовок «${getCellString(headerRow, def.col).slice(0, 40)}» не совпадает с ожидаемым`);
    }
  }

  return warnings;
}

export async function parseRecordExcelFile(
  buffer: ArrayBuffer,
  fileName: string,
): Promise<RecordExcelParseResult> {
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(buffer);

  const sheet = workbook.getWorksheet(RECORD_EXCEL_DATA_SHEET);
  if (!sheet) {
    throw new Error(`Лист «${RECORD_EXCEL_DATA_SHEET}» не найден. Проверьте формат файла.`);
  }

  const headerWarnings = validateHeaders(sheet);
  const rows: RecordExcelParsedRow[] = [];

  for (let rowNumber = RECORD_EXCEL_DATA_START_ROW; rowNumber <= sheet.rowCount; rowNumber += 1) {
    const row = sheet.getRow(rowNumber);
    const values = buildValues(row);
    if (isRowEmpty(values)) continue;

    rows.push({
      rowNumber,
      values,
      warnings: rowWarnings(values),
    });
  }

  if (rows.length === 0) {
    throw new Error('В файле не найдено строк с данными (начиная со строки 7).');
  }

  return {
    fileName,
    sheetName: RECORD_EXCEL_DATA_SHEET,
    headerRow: RECORD_EXCEL_HEADER_ROW,
    dataStartRow: RECORD_EXCEL_DATA_START_ROW,
    rows,
    headerWarnings,
  };
}

export const RECORD_EXCEL_CONTEXT_FIELDS = [
  { dbTable: 'compensation_records' as const, dbField: 'company_id', label: 'Компания (FK)', source: 'индустрия (мастер) + сектор (G) + компания (A)' },
  { dbTable: 'compensation_records' as const, dbField: 'source', label: 'Источник данных', source: 'шаг 2 мастера загрузки' },
  { dbTable: 'compensation_records' as const, dbField: 'added_date', label: 'Дата получения', source: 'дата импорта' },
  { dbTable: 'compensation_records' as const, dbField: 'reliability_score', label: 'Надёжность', source: 'автоматически по источнику' },
];

export function formatDbTarget(table: RecordExcelDbTable, field: string): string {
  return `${table}.${field}`;
}

export const RECORD_EXCEL_COLUMN_PREVIEW: {
  key: keyof RecordExcelParsedValues;
  letter: string;
  label: string;
}[] = [
  { key: 'companyName', letter: 'A', label: 'Компания' },
  { key: 'sectorName', letter: 'G', label: 'Сектор' },
  { key: 'companyRoleName', letter: 'H', label: 'Должность' },
  { key: 'datacircleGrade', letter: 'V', label: 'Грейд' },
  { key: 'familyName', letter: 'W', label: 'Семья' },
  { key: 'roleName', letter: 'Z', label: 'Роль DC' },
  { key: 'baseGrossSalary', letter: 'AE', label: 'Base (год)' },
  { key: 'stiTargetPct', letter: 'AO', label: 'STI %' },
  { key: 'ltipForm', letter: 'AX', label: 'LTIP' },
];
